import { EVENT_ID_PROP_PREFIX, EVENT_TOKEN_PROP_PREFIX, INTERNAL_PROP_PREFIX } from '../../common';
import { EMMA_EVENTS } from './events.constant';

export enum EMMA_KPIS {
  ARPU = 'arpu',
  CLICKS = 'clicks',
  COST = 'cost',
  INSTALLS = 'installs',
  REVENUE = 'revenue',
  REACTIVATIONS = '__reactivations',
  ROI = 'roi',
  UNINSTALLS = 'uninstalls',
}

export enum PURCHASE_TYPE {
  CREATE = 'create_purchase',
  UPDATE = 'update_purchase',
}

export enum EMMA_LEGACY_KPIS {
  // Legacy
  BUYERS = 'buyers',
  CONVERSIONS = 'conversions',
  CPB = 'cpb',
  CPC = 'cpc',
  CPI = 'cpi',
  CPL = 'cpl',
  CPS = 'cps',
  CPA = 'cpa',
  DAILY_BUYERS = 'daily buyers',
  ENGAGEMENT = 'engagement',
  IMPRESSIONS = 'impressions',
  ITR = 'itr',
  LEADS = 'leads',
  REGISTERS = 'registers',
  SALES = 'sales',
  UNIQUE_CLICKS = 'unique_clicks',
}

export enum EMMA_RATIOS {
  INSTALLS_RATIO = '__installsRatio',
  REINSTALLS_RATIO = '__reinstallsRatio',
  LOGIN_RATIO = '__loginRatio',
  RELOGIN_RATIO = '__reloginRatio',
  REACTIVATIONS_RATIO = '__reactivationsRatio',
  REGISTERS_RATIO = '__registersRatio',
  REREGISTERS_RATIO = '__reregistersRatio',
  OPENS_RATIO = '__opensRatio',
  REOPENS_RATIO = '__reopensRatio',
  TRANSACTION_RATIO = '__transactionRatio',
  RETRANSACTION_RATIO = '__retransactionRatio',
  UNINSTALLS_RATIO = '__uninstallsRatio',
  REUNINSTALLS_RATIO = '__reuninstallsRatio',
}

export enum EMMA_COSTS {
  INSTALLS_COST = '__installsCost',
  REINSTALLS_COST = '__reinstallsCost',
  LOGIN_COST = '__loginCost',
  REACTIVATIONS_COST = '__reactivationsCost',
  REGISTERS_COST = '__registersCost',
  OPENS_COST = '__opensCost',
  REOPENS_COST = '__reopensCost',
  TRANSACTION_COST = '__transactionCost',
}

export const EMMA_INTERNAL_PREFIX = INTERNAL_PROP_PREFIX;
export const EMMA_EVENT_TOKEN_PREFIX = EVENT_TOKEN_PROP_PREFIX;
export const EMMA_EVENT_ID_PREFIX = EVENT_ID_PROP_PREFIX;

type Needs = EMMA_COSTS | EMMA_KPIS | EMMA_RATIOS | EMMA_EVENTS;
export const ratiosNeeds: { [key: string]: Needs[] } = Object.freeze({
  [EMMA_KPIS.REACTIVATIONS]: [EMMA_EVENTS.REOPEN, EMMA_EVENTS.REINSTALL],
  [EMMA_RATIOS.INSTALLS_RATIO]: [EMMA_KPIS.INSTALLS, EMMA_KPIS.CLICKS],
  [EMMA_RATIOS.REINSTALLS_RATIO]: [EMMA_EVENTS.REINSTALL, EMMA_KPIS.CLICKS],
  [EMMA_COSTS.INSTALLS_COST]: [EMMA_KPIS.COST, EMMA_KPIS.INSTALLS],
  [EMMA_COSTS.REINSTALLS_COST]: [EMMA_KPIS.COST, EMMA_EVENTS.REINSTALL],
  [EMMA_COSTS.LOGIN_COST]: [EMMA_KPIS.COST, EMMA_EVENTS.LOGIN],
  [EMMA_RATIOS.LOGIN_RATIO]: [EMMA_EVENTS.LOGIN, EMMA_KPIS.INSTALLS],
  [EMMA_COSTS.OPENS_COST]: [EMMA_KPIS.COST, EMMA_EVENTS.SESSION],
  [EMMA_RATIOS.OPENS_RATIO]: [EMMA_EVENTS.SESSION, EMMA_KPIS.CLICKS],
  [EMMA_COSTS.REACTIVATIONS_COST]: [
    EMMA_KPIS.COST,
    EMMA_KPIS.REACTIVATIONS,
    EMMA_EVENTS.REOPEN,
    EMMA_EVENTS.REINSTALL,
  ],
  [EMMA_RATIOS.REACTIVATIONS_RATIO]: [
    EMMA_KPIS.REACTIVATIONS,
    EMMA_KPIS.CLICKS,
    EMMA_EVENTS.REOPEN,
    EMMA_EVENTS.REINSTALL,
  ],
  [EMMA_COSTS.REGISTERS_COST]: [EMMA_KPIS.COST, EMMA_EVENTS.REGISTER],
  [EMMA_RATIOS.REGISTERS_RATIO]: [EMMA_EVENTS.REGISTER, EMMA_KPIS.INSTALLS],
  [EMMA_RATIOS.RELOGIN_RATIO]: [
    EMMA_EVENTS.LOGIN,
    EMMA_KPIS.REACTIVATIONS,
    EMMA_EVENTS.REOPEN,
    EMMA_EVENTS.REINSTALL,
  ],
  [EMMA_COSTS.REOPENS_COST]: [EMMA_KPIS.COST, EMMA_EVENTS.REOPEN],
  [EMMA_RATIOS.REOPENS_RATIO]: [EMMA_EVENTS.REOPEN, EMMA_KPIS.CLICKS],
  [EMMA_RATIOS.REREGISTERS_RATIO]: [
    EMMA_EVENTS.REGISTER,
    EMMA_KPIS.REACTIVATIONS,
    EMMA_EVENTS.REOPEN,
    EMMA_EVENTS.REINSTALL,
  ],
  [EMMA_RATIOS.RETRANSACTION_RATIO]: [
    EMMA_EVENTS.TRANSACTION,
    EMMA_KPIS.REACTIVATIONS,
    EMMA_EVENTS.REOPEN,
    EMMA_EVENTS.REINSTALL,
  ],
  [EMMA_COSTS.TRANSACTION_COST]: [EMMA_KPIS.COST, EMMA_EVENTS.TRANSACTION],
  [EMMA_RATIOS.TRANSACTION_RATIO]: [EMMA_EVENTS.TRANSACTION, EMMA_KPIS.INSTALLS],
  [EMMA_RATIOS.UNINSTALLS_RATIO]: [EMMA_KPIS.INSTALLS, EMMA_KPIS.UNINSTALLS],
  [EMMA_RATIOS.REUNINSTALLS_RATIO]: [
    EMMA_KPIS.REACTIVATIONS,
    EMMA_KPIS.UNINSTALLS,
    EMMA_EVENTS.REOPEN,
    EMMA_EVENTS.REINSTALL,
  ],
});

export const EMMA_SDK_MANUALS = ((root: string) =>
  Object.freeze({
    IOS_COCOA_OBJECTIVEC: root + 'ios-cocoa-objectivec-{LANG}.md',
    IOS_COCOA_SWIFT: root + 'ios-cocoa-swift-{LANG}.md',
    ANDROID_GRADLE_JAVA: root + 'android-gradle-java-{LANG}.md',
    ANDROID_GRADLE_KOTLIN: root + 'android-gradle-kotlin-{LANG}.md',
    CROSSPLATFORM_XAMARIN: root + 'crossplatform-xamarin-{LANG}.md',
    CROSSPLATFORM_CORDOVA: root + 'crossplatform-cordova-{LANG}.md',
    CROSSPLATFORM_PHONEGAP: root + 'crossplatform-phonegap-{LANG}.md',
    CROSSPLATFORM_IONIC: root + 'crossplatform-ionic-{LANG}.md',
    CROSSPLATFORM_REACTNATIVE: root + 'crossplatform-reactnative-{LANG}.md',
    CROSSPLATFORM_FLUTTER: root + 'crossplatform-flutter-{LANG}.md',
  }))('https://raw.githubusercontent.com/EMMADevelopment/quickstart-docs/master/');

export const MIN_PASSWORD_LENGTH = 12;

export enum EMMA_API_VERSION {
  V10 = '1.0',
  V20 = '2.0',
}

export enum TRACKER_TYPE {
  BASIC = 'basic',
  RETARGETING = 'retargeting',
  ALL = 'all',
}

export enum SHOW_TRACKER {
  ALL = 'all',
  FAVORITES = 'favorites',
}

export const SECRET_TEXT_FOR_PRIVATE_KEY = '(is secret)';

export enum PAY_MODEL {
  CPC = 'cpc',
  CPI = 'cpi',
  CPL = 'cpl',
  CPA = 'cpa',
  FIXED = 'fixed',
}

export const DEFAULT_RT_WINDOW_SIZE = 30;
export const MAX_RT_WINDOW_SIZE = 90;

export enum DEVICE_ID_TYPE {
  IDFA = 'idfa',
  IDVF = 'idfv',
  GAID = 'gaid',
  OAID = 'oaid',
  AUTO = 'auto',
}

export type InputType = 'text' | 'number' | 'url' | 'email' | 'emmaurl' | 'urlparam' | 'deeplink';

export const EMPTY = '_empty_';
